/**
 * CLR History Tab — PLR-style card list with stats bar, filters, and pagination.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiSearch, FiClock, FiX, FiChevronLeft, FiChevronRight,
  FiCheck, FiAlertTriangle, FiLayers, FiArrowRight,
} from 'react-icons/fi';
import { RiSparklingFill } from 'react-icons/ri';
import { fetchCommercialHistory } from './useCommercialRating';

interface Props {
  onOpenRun: (runId: string, insuredName: string, lob?: string) => void;
}

const PER_PAGE = 15;

function fmtDate(d?: string | null) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
}
function fmtTime(d?: string | null) {
  if (!d) return '';
  try { return new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); }
  catch { return ''; }
}
function fmtDur(ms?: number | null) {
  if (!ms || ms <= 0) return '';
  const s = Math.round(ms / 1000);
  return s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;
}
function initials(name: string) {
  return (name || '?').split(/[\s,]+/).filter(Boolean).map(w => w[0]?.toUpperCase()).join('').slice(0, 2) || '?';
}

const LOB_META: Record<string, { label: string; cls: string }> = {
  WC:    { label: 'Workers Comp', cls: 'wc' },
  Cyber: { label: 'Cyber Policy', cls: 'cyber' },
  GL:    { label: 'General Liability', cls: 'gl' },
  BOP:   { label: 'Business Owners Policy', cls: 'bop' },
};
function lobMeta(lob?: string) {
  return LOB_META[lob || 'WC'] || LOB_META.WC;
}

const RateMIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.63 13.16c0-1.55.01-3.1 0-4.65-.01-.83.3-1.52.84-2.11.94-1.02 1.92-2 2.83-3.03.46-.52 1.69-.47 2.01.31.06.14.1.3.1.45.01 3.08.01 6.15 0 9.23-.01.79-.38 1.46-.9 2.03-.98 1.08-1.99 2.13-3.01 3.17-.5.51-1.22.58-1.63.14-.16-.18-.23-.5-.24-.76-.02-1.59-.01-3.18-.01-4.77z" fill="currentColor"/>
    <path d="M21.91 8.78c0 1.5-.01 3-.01 4.5 0 .71-.23 1.33-.71 1.84-1.05 1.12-2.09 2.25-3.17 3.35-.23.24-.58.44-.9.49-.55.09-1.01-.4-1.01-1.02 0-3.17 0-6.34.02-9.5 0-.78.29-1.45.85-2.03.9-.94 1.78-1.9 2.65-2.87.42-.47.9-.68 1.52-.46.5.17.75.51.75 1.04v4.65z" fill="currentColor"/>
    <path d="M8.87 12.29c-.08 2.46.09 2-.4 3.45-.81.88-1.63 1.76-2.46 2.62-.22.23-.49.45-.78.56-.64.26-1.28-.19-1.29-.89-.01-.8-.04-1.59.02-2.38.02-.32.18-.69.4-.93 1.25-1.39 2.54-2.75 3.82-4.13.25-.27.51-.48.9-.46.45.02.71.29.72.75 0 .52 0 1.04 0 1.41z" fill="currentColor"/>
  </svg>
);

const CLRHistoryTab: React.FC<Props> = ({ onOpenRun }) => {
  const [data, setData]             = useState<any[]>([]);
  const [stats, setStats]           = useState({ total: 0, quoted: 0, failed: 0, running: 0, awaiting: 0 });
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage]             = useState(1);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterLob, setFilterLob]   = useState('');
  const [dateFrom, setDateFrom]     = useState('');
  const [dateTo, setDateTo]         = useState('');
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const statsLoaded = useRef(false);

  const fetchPage = useCallback(async (
    p: number, s: string, fs: string, fl: string, df: string, dt: string,
  ) => {
    setLoading(true);
    try {
      const res = await fetchCommercialHistory({
        limit: PER_PAGE,
        offset: (p - 1) * PER_PAGE,
        search: s || undefined,
        status: fs || undefined,
        lob: fl || undefined,
        dateFrom: df || undefined,
        dateTo: dt || undefined,
      });
      setData(res.runs ?? []);
      setTotal(res.total ?? 0);
      setTotalPages(Math.max(1, Math.ceil((res.total ?? 0) / PER_PAGE)));
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  const fetchStats = useCallback(async () => {
    if (statsLoaded.current) return;
    try {
      const [all, quoted, failed, running, awaiting] = await Promise.all([
        fetchCommercialHistory({ limit: 0 }),
        fetchCommercialHistory({ limit: 0, status: 'completed' }),
        fetchCommercialHistory({ limit: 0, status: 'failed' }),
        fetchCommercialHistory({ limit: 0, status: 'running' }),
        fetchCommercialHistory({ limit: 0, status: 'awaiting_input' }),
      ]);
      setStats({
        total:   all.total,
        quoted:  quoted.total,
        failed:  failed.total,
        running: running.total,
        awaiting: awaiting.total,
      });
      statsLoaded.current = true;
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchPage(1, '', '', '', '', '');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearchChange = (v: string) => {
    setSearch(v);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(1);
      fetchPage(1, v, filterStatus, filterLob, dateFrom, dateTo);
    }, 400);
  };

  const applyFilter = (fs: string) => {
    setFilterStatus(fs);
    setPage(1);
    fetchPage(1, search, fs, filterLob, dateFrom, dateTo);
  };

  const applyLobFilter = (fl: string) => {
    setFilterLob(fl);
    setPage(1);
    fetchPage(1, search, filterStatus, fl, dateFrom, dateTo);
  };

  const applyDate = (df: string, dt: string) => {
    setPage(1);
    fetchPage(1, search, filterStatus, filterLob, df, dt);
  };

  const clearAll = () => {
    setSearch(''); setFilterStatus(''); setFilterLob('');
    setDateFrom(''); setDateTo(''); setPage(1);
    fetchPage(1, '', '', '', '', '');
  };

  const goPage = (p: number) => {
    setPage(p);
    fetchPage(p, search, filterStatus, filterLob, dateFrom, dateTo);
  };

  const hasFilter = !!(search || filterStatus || filterLob || dateFrom || dateTo);

  const statCards = [
    { label: 'Total Runs', val: stats.total,   icon: <FiLayers size={13} />,       accent: '#a200ff' },
    { label: 'Quoted',     val: stats.quoted,   icon: <FiCheck size={13} />,        accent: '#22c55e' },
    { label: 'Failed',     val: stats.failed,   icon: <FiAlertTriangle size={13} />, accent: '#ef4444' },
    { label: 'Running',    val: stats.running,  icon: <RiSparklingFill size={13} />, accent: '#818cf8' },
    { label: 'Awaiting',   val: stats.awaiting, icon: <FiClock size={13} />,        accent: '#f59e0b' },
  ];

  return (
    <div className="rql-hist">

      {/* ── Stats bar ── */}
      <div className="rql-hist__stats">
        {statCards.map((s, i) => (
          <div key={i} className="rql-hist__stat" style={{ '--stat-accent': s.accent } as React.CSSProperties}>
            <div className="rql-hist__stat-icon" style={{ color: s.accent }}>{s.icon}</div>
            <div className="rql-hist__stat-body">
              <span className="rql-hist__stat-val">{s.val}</span>
              <span className="rql-hist__stat-lbl">{s.label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ── Filters ── */}
      <div className="rql-hist__filters">
        <div className="rql-hist__search">
          <FiSearch size={13} />
          <input
            type="text"
            placeholder="Search insured name..."
            value={search}
            onChange={e => onSearchChange(e.target.value)}
          />
          {search && (
            <button className="rql-hist__date-clear" onClick={() => onSearchChange('')}>
              <FiX size={11} />
            </button>
          )}
        </div>

        <div className="rql-hist__pills">
          {([
            { val: '',               label: 'All' },
            { val: 'completed',      label: 'Quoted' },
            { val: 'failed',         label: 'Failed' },
            { val: 'awaiting_input', label: 'Awaiting' },
          ] as const).map(({ val, label }) => (
            <button
              key={val || 'all'}
              className={`rql-hist__pill${filterStatus === val ? ' rql-hist__pill--active' : ''}`}
              onClick={() => applyFilter(val)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="rql-hist__pills">
          {([
            { val: '',      label: 'All LOBs' },
            { val: 'WC',    label: 'Workers Comp' },
            { val: 'Cyber', label: 'Cyber' },
            { val: 'GL',    label: 'Gen. Liability' },
            { val: 'BOP',   label: 'BOP' },
          ] as const).map(({ val, label }) => (
            <button
              key={val || 'all-lob'}
              className={`rql-hist__pill${filterLob === val ? ' rql-hist__pill--active' : ''}`}
              onClick={() => applyLobFilter(val)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="rql-hist__date-range">
          <FiClock size={11} />
          <input
            type="date"
            value={dateFrom}
            onChange={e => { setDateFrom(e.target.value); applyDate(e.target.value, dateTo); }}
          />
          <span className="rql-hist__date-sep">–</span>
          <input
            type="date"
            value={dateTo}
            onChange={e => { setDateTo(e.target.value); applyDate(dateFrom, e.target.value); }}
          />
          {(dateFrom || dateTo) && (
            <button className="rql-hist__date-clear" onClick={() => { setDateFrom(''); setDateTo(''); applyDate('', ''); }}>
              <FiX size={11} />
            </button>
          )}
        </div>

        <span className="rql-hist__count">{total} run{total !== 1 ? 's' : ''}</span>
      </div>

      {/* ── Card list ── */}
      {loading ? (
        <div className="rql-hist__list">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rql-hist__card rql-hist__skel-card">
              <div className="rql-hist__card-left">
                <div className="rql-hist__skel rql-hist__skel--avatar" />
                <div className="rql-hist__card-ident">
                  <div className="rql-hist__skel rql-hist__skel--name" />
                  <div className="rql-hist__skel rql-hist__skel--meta" />
                </div>
              </div>
              <div className="rql-hist__card-right">
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5 }}>
                  <div className="rql-hist__skel rql-hist__skel--price" />
                  <div className="rql-hist__skel rql-hist__skel--carrier" />
                </div>
                <div className="rql-hist__skel rql-hist__skel--badge" />
              </div>
            </div>
          ))}
        </div>
      ) : data.length === 0 ? (
        <div className="rql-hist__empty">
          <div className="rql-hist__empty-icon">
            {hasFilter ? <FiSearch size={32} /> : <FiClock size={32} />}
          </div>
          <span className="rql-hist__empty-title">
            {hasFilter ? 'No runs found' : 'No run history yet'}
          </span>
          <span className="rql-hist__empty-hint">
            {hasFilter
              ? 'Try adjusting your search, filters, or date range.'
              : 'Run a quote from the Prospects tab to see history here.'}
          </span>
          {hasFilter && (
            <button className="rql-hist__empty-clear" onClick={clearAll}>
              <FiX size={12} /> Clear all filters
            </button>
          )}
        </div>
      ) : (
        <div className="rql-hist__list">
          {data.map(r => {
            const inits = initials(r.insured_name || '');
            const lm = lobMeta(r.lob);
            const allQuotes: any[] = r.result_payload?.quotes ?? [];
            const quoted = allQuotes
              .filter((q: any) => q.statusCode === 'QUOTED' && q.premiumAmount > 0)
              .sort((a: any, b: any) => a.premiumAmount - b.premiumAmount);
            const bridged = allQuotes.filter((q: any) =>
              q.requiresBridging && q.statusCode !== 'DECLINED' && q.statusCode !== 'FAILED'
              && !quoted.some((qq: any) => qq.carrierId === q.carrierId)
            );
            const best = quoted[0] ?? null;
            const quotedCount = quoted.length + bridged.length;
            const dur = fmtDur(r.duration_ms);

            return (
              <div
                key={r.id}
                className={`rql-hist__card rql-hist__card--${r.status}`}
                onClick={() => onOpenRun(r.id, r.insured_name || '', r.lob)}
                style={{ cursor: 'pointer' }}
              >
                {/* Left: avatar + ident */}
                <div className="rql-hist__card-left">
                  <div className={`rql-hist__avatar rql-hist__avatar--${lm.cls}`}>{inits}</div>
                  <div className="rql-hist__card-ident">
                    <div className="rql-hist__card-name">
                      {r.insured_name || '—'}
                      {r.insured_state && (
                        <span className="rql-hist__state-badge">{r.insured_state}</span>
                      )}
                    </div>
                    <div className="rql-hist__card-meta">
                      <span className={`rql-hist__type rql-hist__type--${lm.cls}`}>{lm.label}</span>
                      <span className="rql-hist__meta-dot" />
                      <span>{fmtDate(r.started_at)}</span>
                      {fmtTime(r.started_at) && (
                        <><span className="rql-hist__meta-dot" /><span>{fmtTime(r.started_at)}</span></>
                      )}
                      {dur && (
                        <><span className="rql-hist__meta-dot" /><FiClock size={9} /><span>{dur}</span></>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right: premium + status */}
                <div className="rql-hist__card-right">
                  {best ? (
                    <div className="rql-hist__card-premium">
                      <span className="rql-hist__card-amount">
                        ${best.premiumAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        <span className="rql-hist__card-term">/yr</span>
                      </span>
                      <span className="rql-hist__card-carrier">
                        {best.carrierName}
                        {allQuotes.length > 1 && (
                          <span className="rql-hist__card-qcount">
                            {' '}· {quotedCount}/{allQuotes.length} quoted
                          </span>
                        )}
                      </span>
                    </div>
                  ) : allQuotes.length > 0 ? (
                    <div className="rql-hist__card-premium rql-hist__card-premium--none">
                      <span className="rql-hist__card-no-quote">No premiums</span>
                      <span className="rql-hist__card-carrier">
                        {allQuotes.length} carrier{allQuotes.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  ) : null}
                  <div className="rql-hist__card-actions">
                    <span className={`rql-hist__status rql-hist__status--${r.status}`}>
                      {r.status === 'completed'      ? 'Quoted'
                        : r.status === 'failed'       ? 'Failed'
                        : r.status === 'running'      ? 'Running'
                        : 'Awaiting'}
                    </span>
                    {r.application_id && (r.status === 'completed' || r.status === 'failed') && (
                      <button type="button" className="rql-iwf__go-to-rate" onClick={async e => {
                        e.stopPropagation();
                        try {
                          const tok = localStorage.getItem('authToken') || '';
                          const res = await fetch(`${process.env.REACT_APP_MyAMP_Url}/api/ams/commercial-rating/runs/${r.id}/rate-url`, { headers: { Authorization: `Bearer ${tok}` } });
                          if (res.ok) { const d = await res.json(); if (d.rate_url) window.open(d.rate_url, '_blank'); }
                        } catch { /* silently fail */ }
                      }}><RateMIcon size={12} /> Go to Rate</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="rql-hist__pagination">
          <button disabled={page <= 1} onClick={() => goPage(1)}>
            <FiChevronLeft size={14} /><FiChevronLeft size={14} style={{ marginLeft: -8 }} />
          </button>
          <button disabled={page <= 1} onClick={() => goPage(page - 1)}>
            <FiChevronLeft size={14} />
          </button>
          <span className="rql-hist__page-info">Page {page} of {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => goPage(page + 1)}>
            <FiChevronRight size={14} />
          </button>
          <button disabled={page >= totalPages} onClick={() => goPage(totalPages)}>
            <FiChevronRight size={14} /><FiChevronRight size={14} style={{ marginLeft: -8 }} />
          </button>
        </div>
      )}
    </div>
  );
};

export default CLRHistoryTab;
